import styled, { css, keyframes } from 'styled-components'
import {
  AutocompleteWrapperProps,
  AutocompleteDropdownProps,
  AutocompleteOptionProps,
  AutocompleteCategoryProps,
  AutocompleteSelectedTagsProps,
  AutocompleteTagProps,
} from './Autocomplete.types'
import { InputSize } from '../Input/Input.types'

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

export const AutocompleteWrapper = styled.div<AutocompleteWrapperProps>`
  position: relative;
  display: ${(props) => (props.$fullWidth ? 'block' : 'inline-block')};
  width: ${(props) => (props.$fullWidth ? '100%' : 'auto')};
  z-index: ${(props) => (props.$isOpen ? 9999999 : 'auto')};
  isolation: ${(props) => (props.$isOpen ? 'isolate' : 'auto')};
`

export const AutocompleteDropdown = styled.div<AutocompleteDropdownProps>`
  position: ${({ $layout }) => ($layout === 'inline' ? 'static' : 'absolute')};
  margin-top: ${({ $layout, theme }) =>
    $layout === 'inline' ? theme.spacing.xsmall : 0};
  top: calc(100% + ${({ theme }) => theme.spacing.xsmall});
  left: 0;
  right: 0;
  width: ${(props) => (props.$fullWidth ? '100%' : 'auto')};
  min-width: 200px;
  max-width: 100%;
  background-color: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.shadows.md};
  z-index: 10;
  overflow: hidden;
  animation: ${fadeIn} ${({ theme }) => theme.transitions.fast};

  ${(props) =>
    props.$isOpen &&
    css`
      display: block;
    `}

  ${(props) =>
    !props.$isOpen &&
    css`
      display: none;
    `}

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    left: 0;
    right: 0;
    width: 100%;
  }
`

export const AutocompleteOptionsList = styled.div`
  max-height: 300px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.background.secondary};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border.default};
    border-radius: ${({ theme }) => theme.borderRadius.small};
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.border.light};
  }
`

export const AutocompleteOptionItem = styled.div<AutocompleteOptionProps>`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.medium};
  cursor: pointer;
  transition: background-color ${({ theme }) => theme.transitions.fast};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  color: ${({ theme }) => theme.colors.text.primary};
  border: none;
  background: none;

  ${(props) =>
    props.$disabled &&
    css`
      cursor: not-allowed;
      opacity: 0.5;
      color: ${({ theme }) => theme.colors.text.tertiary};
    `}

  ${(props) =>
    props.$isHighlighted &&
    !props.$disabled &&
    css`
      background-color: ${({ theme }) => theme.colors.background.secondary};
    `}

  ${(props) =>
    props.$isSelected &&
    css`
      background-color: ${({ theme }) => theme.colors.primary}10;
      font-weight: ${({ theme }) => theme.typography.fontWeight.medium};

      &::after {
        content: '✓';
        margin-left: auto;
        color: ${({ theme }) => theme.colors.primary};
        font-weight: bold;
      }
    `}

  &:hover:not([aria-disabled="true"]) {
    background-color: ${({ theme }) => theme.colors.background.secondary};
  }

  &:active:not([aria-disabled='true']) {
    background-color: ${({ theme }) => theme.colors.background.tertiary};
  }
`

export const AutocompleteCategoryHeader = styled.div<AutocompleteCategoryProps>`
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.medium};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.secondary};
  background-color: ${({ theme }) => theme.colors.background.secondary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${(props) =>
    !props.$isFirst &&
    css`
      border-top: 1px solid ${({ theme }) => theme.colors.border.default};
    `}
`

export const AutocompleteNoOptions = styled.div`
  padding: ${({ theme }) => theme.spacing.medium};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-style: italic;
  font-size: ${({ theme }) => theme.typography.fontSize.body};
`

export const AutocompleteLoading = styled.div`
  padding: ${({ theme }) => theme.spacing.medium};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.body};

  svg {
    animation: ${spin} 1s linear infinite;
  }
`

// Multiple selection styles

export const AutocompleteSelectedTags = styled.div<AutocompleteSelectedTagsProps>`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  width: 100%;
  background-color: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  transition: all ${({ theme }) => theme.transitions.fast};
  cursor: text;
  font-family: ${({ theme }) => theme.typography.fontFamily.sans};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  color: ${({ theme }) => theme.colors.text.primary};

  min-height: ${(props) => {
    switch (props.$size) {
      case 'small':
        return '32px'
      case 'large':
        return '48px'
      default:
        return '40px'
    }
  }};

  padding: ${(props) => {
    switch (props.$size) {
      case 'small':
        return `0 36px 0 ${props.theme.spacing.small}`
      case 'large':
        return `0 52px 0 ${props.theme.spacing.large}`
      default:
        return `0 44px 0 ${props.theme.spacing.medium}`
    }
  }};

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.border.light};
  }

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.border.focus};
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.background.tertiary};
    color: ${({ theme }) => theme.colors.text.tertiary};
    cursor: not-allowed;
    opacity: 0.6;
  }
`

export const AutocompleteTag = styled.div<AutocompleteTagProps>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  padding: ${(props) => {
    switch (props.$size) {
      case 'small':
        return `2px ${props.theme.spacing.xsmall}`
      case 'large':
        return `4px ${props.theme.spacing.small}`
      default:
        return `3px ${props.theme.spacing.xsmall}`
    }
  }};
  background-color: ${({ theme }) => theme.colors.primary}15;
  border: 1px solid ${({ theme }) => theme.colors.primary}30;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${(props) => {
    switch (props.$size) {
      case 'small':
        return '11px'
      case 'large':
        return '13px'
      default:
        return '12px'
    }
  }};
  color: ${({ theme }) => theme.colors.text.primary};
  max-width: 200px;
  line-height: 1.2;

  /* Ensure text label takes available space but leaves room for remove button */
  > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
  }

  /* Ensure remove button always visible */
  > button {
    flex-shrink: 0;
  }
`

export const AutocompleteTagRemove = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  border-radius: 50%;
  width: 14px;
  height: 14px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.secondary};
  transition: all ${({ theme }) => theme.transitions.fast};
  flex-shrink: 0;

  &:hover {
    background-color: ${({ theme }) => theme.colors.danger}20;
    color: ${({ theme }) => theme.colors.danger};
  }

  &:focus {
    outline: none;
    background-color: ${({ theme }) => theme.colors.danger}20;
    color: ${({ theme }) => theme.colors.danger};
  }

  svg {
    width: 10px;
    height: 10px;
  }
`

export const AutocompleteInlineInput = styled.input<{ $size: InputSize }>`
  flex: 1;
  min-width: 120px;
  border: none;
  outline: none;
  background: transparent;
  font-family: inherit;
  padding: 0;
  margin: 0;
  font-size: ${(props) => {
    switch (props.$size) {
      case 'small':
        return props.theme.typography.fontSize.small
      case 'large':
        return props.theme.typography.fontSize.section
      default:
        return props.theme.typography.fontSize.body
    }
  }};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.5;

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  &:disabled {
    cursor: not-allowed;
    color: ${({ theme }) => theme.colors.text.tertiary};
  }
`

export const AutocompleteRightIcon = styled.div<{ $size: InputSize }>`
  position: absolute;
  top: 50%;
  right: ${(props) => {
    switch (props.$size) {
      case 'small':
        return props.theme.spacing.small
      case 'large':
        return props.theme.spacing.medium
      default:
        return '10px'
    }
  }};
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  color: ${({ theme }) => theme.colors.text.secondary};
`
